import { useCallback, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { selectTerritories } from '@/features/domain/territory'

import { useTexts } from '../hooks/useTexts'
import { createValidateTerritoryFilter } from '../hooks/validations/createValidateTerritoryFilter'

import { form } from '../formState'

export function TerritoryFilter() {
  const texts = useTexts()
  const { options, getOptionLabel } = useTerritoriesFilterData()
  const formApi = form.useFormApi()
  const type = form.useFormField('type')
  const limitedAccess = form.useFormField('limitedAccess')

  useEffect(() => {
    const unsubscribe = formApi.subscribeToField('limitedAccess', field => {
      // toggle the required state of the territoryFilter field
      // changing the required state will invalidate the field
      if (!field.value) {
        formApi.clearError('territoryFilterRequiredError')
        formApi.change('territoryFilter', [])
      }
    })

    return () => {
      unsubscribe()
    }
  }, [formApi])

  form.useAddFieldValidation<'territoryFilter'>(
    'territoryFilter',
    (field, _formApi) => {
      const validationTerritoryFilter = createValidateTerritoryFilter(
        texts,
        field.value,
        type.field.value,
        limitedAccess.field.value,
      )
      return validationTerritoryFilter.territoryFilterRequiredError?.message
    },
    { customUID: 'territoryFilterRequiredError' },
  )

  return (
    <form.MultiSelect
      name="territoryFilter"
      testId="territoryFilter"
      validateOn="blur change"
      multiSelectProps={{
        label: texts.labels.territoriesFilter,
        items: options,
        getLabel: getOptionLabel,
        inputMode: 'normal',
        optionMode: 'normal',
        texts: texts.multiSelect.territories,
        autocompleteProps: {
          isOptionEqualToValue: (o1, o2) => o1.id === o2.id,
        },
      }}
    />
  )
}

export function useTerritoriesFilterData() {
  const territories = useSelector(selectTerritories)
  const { api, values } = form.useForm()

  const options = useMemo(() => Object.values(territories), [territories])

  const getOptionLabel = useCallback(option => option.name, [])

  const value = useMemo(() => values.territoryFilter, [values])

  const onChange = useCallback(
    (_, territories: uui.domain.client.rm.Territory[] | null) => {
      if (!territories) return

      const value = [...territories]
      api.change('territoryFilter', value)
    },
    [api],
  )

  const territoriesFilterVisible = api.getField('territoryFilter')?.visible
  const hasError = api.getField('territoryFilter')?.status === 'invalid'

  return {
    options,
    onChange,
    getOptionLabel,
    value,
    territoriesFilterVisible,
    hasError,
  }
}
